import React, { createContext, FC, useEffect, useState } from "react";
import cn from "clsx";
import Scrollbars from "react-custom-scrollbars";
import isEmpty from "lodash/isEmpty";
import { useTranslation } from "react-i18next";
import {
  selectIsProductMultisize,
  selectProductDetails,
  selectProductIsSingleSize,
  selectProductSizeChart,
  selectProductSizes
} from "../selectors";
import SlideBottomToTop from "../../../components/slide-bottom-to-top";
import View from "@/components/common/view";
import { ProductDefaultAttributeListNoName } from "../../../components/attributeHandlers";
import ProductModalSize from "./productModalSizes";
import ProductSize from "./productSizes";
import { ProductSize as ProductSizeType } from "@/services/productService";
import { fetchCartData } from "../../../redux/actions/cart.action";
import useBodyScrollability from "../../../hooks/useBodyScrollability";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { determineIsSizeChart } from "@/util/add-to-cart";
import { ProductMobileSizesProps } from "./types";
import Ruler from "@/images/icons/ruler.svg";
import "./style.scss";

const ProductMobileSizes: FC<ProductMobileSizesProps> = ({
  activeSizeType,
  setActiveSizeType,
  selectedSizeFullData,
  onSizeSelectionHandler,
  showProductSizeChart,
  openMultipleSizes,
  setOpenMultipleSizes,
  onSizeSelectionModalClose
}) => {
  const { t } = useTranslation("productDetails");
  const productSizes = useAppSelector(selectProductSizes);
  const isProductMultisize = useAppSelector(selectIsProductMultisize);
  const { highlights, modelProfile } = useAppSelector(selectProductDetails);
  const productIsSingleSize = useAppSelector(selectProductIsSingleSize);
  const { enableBodyScroll } = useBodyScrollability();
  const sizeChart = useAppSelector(selectProductSizeChart);
  const isSizeChart = determineIsSizeChart(sizeChart);
  const dispatch = useAppDispatch();

  const attributesWithSpecialStyles = [
    "bullets",
    "attributes",
    "sizeShown",
    "globalShipping"
  ];
  const productDefaultStyledAttributes = {
    ...highlights
  };
  attributesWithSpecialStyles.forEach(
    key => delete productDefaultStyledAttributes[key]
  );

  const productSizeExtraData = !isEmpty(productDefaultStyledAttributes) && (
    <li
      className={cn("border border-white h-auto sticky_item", {
        multi_size: isProductMultisize
      })}
    >
      <ProductDefaultAttributeListNoName
        attributes={
          productDefaultStyledAttributes as {
            fit: {
              values: {
                text: string;
                icon: string;
              }[];
            };
          }
        }
        listItemClassName="size_extra_data px-2 py-1 d-flex justify-content-between align-items-center"
      />
    </li>
  );

  const closeSizesModalHandler = () => {
    onSizeSelectionModalClose();
    setOpenMultipleSizes(false);
    enableBodyScroll();
  };

  const onSizeSelection = size => {
    onSizeSelectionHandler({ sizeType: activeSizeType, size });
    closeSizesModalHandler();
  };

  const onSizeTypeSelection = sizeType => setActiveSizeType(sizeType);

  useEffect(() => {
    dispatch(fetchCartData());
  }, []);

  const mobileProductHighlights = {
    ...(isSizeChart && {
      sizeShown: {
        values: [
          {
            icon: Ruler,
            text: `${t("needSizing")} ${t("referToSizeChart")}`
          }
        ]
      }
    }),
    ...highlights
  };

  return (
    !productIsSingleSize && (
      <View.mobile className="productSizes mobile_pdp_sizes">
        <MobileSizesContext.Provider
          value={{
            productSizes,
            selectedSizeFullData,
            onSizeSelection,
            showProductSizeChart,
            activeSizeType,
            isProductMultisize,
            onSizeTypeSelection,
            productSizeExtraData,
            sizeShown: highlights?.sizeShown,
            openMultipleSizes
          }}
        >
          <ProductSize
            modelProfile={modelProfile}
            sizeShown={highlights?.sizeShown}
            isProductMultisize={isProductMultisize}
            activeSizeType={activeSizeType}
            productSizes={productSizes}
          />
          <div
            className={cn("slideTop", "multipleSizeContainer", {
              "slideUp fullHeight": openMultipleSizes,
              slideBack: !openMultipleSizes
            })}
          >
            <div className="backOverlay" onClick={closeSizesModalHandler} />
            <>
              <p className="sizeText">{t("selectSize")}</p>
              <span className="mCrossIcon" onClick={closeSizesModalHandler} />
            </>
            <Scrollbars autoHeight autoHeightMax={320}>
              <SlideBottomToTop>
                <ProductModalSize
                  productSizes={productSizes}
                  isProductMultisize={isProductMultisize}
                  activeSizeType={activeSizeType}
                  mobileProductHighlights={mobileProductHighlights}
                  showProductSizeChart={showProductSizeChart}
                />
              </SlideBottomToTop>
            </Scrollbars>
          </div>
        </MobileSizesContext.Provider>
      </View.mobile>
    )
  );
};

export const MobileSizesContext = createContext<{
  productSizes: ProductSizeType[];
  selectedSizeFullData: any;
  onSizeSelection: (size: any) => void;
  showProductSizeChart: (showProductSizeChart: boolean) => void;
  activeSizeType: TextAttributeField | EmptyObject;
  isProductMultisize: boolean;
  onSizeTypeSelection: (sizeType: any) => void;
  productSizeExtraData: React.JSX.Element;
  openMultipleSizes: boolean;
  sizeShown: {
    values: {
      text: string;
      icon: string;
    }[];
  };
}>(null);

export default ProductMobileSizes;
